import { library } from '@fortawesome/fontawesome-svg-core';
import { faUnlink } from '@fortawesome/free-solid-svg-icons';
import { t } from 'i18next';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { S_Alert } from '../../../../design/alert/S_Alert';
import { IOverlayLoaderStore } from '../../../../modules/common/OverlayLoader.store';
import { IProjectDataset } from '../projectDetails.models';
import { ProjectDetailsDatasetsTable } from './ProjectDetailsDatasetsTable';
import { UnlinkDatasetModal } from './UnlinkDatasetModal';

const loaderId = 'unlink-dataset-button';

library.add(faUnlink);

interface IProps {
  workspaceId: string;
  projectDatasets: IProjectDataset[];
  isLoading: boolean;
  onDatasetUnlinked: (datasetId: string) => Promise<void>;
  onDatasetOrderChange: (from: number, to: number) => Promise<void>;
}

export const ProjectDetailsDatasetsList = observer((props: IProps): ReactElement => {
  const overlayLoaderStore = useInjection(IOverlayLoaderStore.$);

  const { workspaceId, projectDatasets, isLoading, onDatasetUnlinked, onDatasetOrderChange } = props;

  const [showModal, setShowModal] = useState<boolean>(false);
  const [datasetId, setDatasetId] = useState<string | undefined>(undefined);

  const toggleModal = (id: string): void => {
    setShowModal(true);
    setDatasetId(id);
  };

  const hideModal = (): void => {
    setShowModal(false);
    setDatasetId(undefined);
  };

  const handleConfirm = async (): Promise<void> => {
    if (datasetId) {
      overlayLoaderStore.enableLoader(loaderId);
      await onDatasetUnlinked(datasetId);
      hideModal();
      overlayLoaderStore.disableLoader(loaderId);
    }
  };

  const handleCancel = (): void => {
    hideModal();
    overlayLoaderStore.disableLoader(loaderId);
  };

  const sizeSumInB = projectDatasets.map((x) => x.imageSizeInB).reduce((a, b) => a + b, 0);

  return (
    <>
      {sizeSumInB >= 1024 ** 3 && <S_Alert color='warning'>{t('project_size_limit')}</S_Alert>}
      <ProjectDetailsDatasetsTable
        workspaceId={workspaceId}
        projectDatasets={projectDatasets}
        isLoading={isLoading}
        onUnlink={toggleModal}
        onDatasetOrderChange={onDatasetOrderChange}
      />
      <UnlinkDatasetModal
        show={showModal}
        hideModal={hideModal}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
});
